// Capture setup copy, shared by the Settings screen and "Email these steps to me", so both say
// the same thing in the same order. Plain words, short imperative sentences, no em dashes.
import { GENERIC_BANK_STEPS, type GmailFilter } from './banks';

export const SETUP_INTRO = [
  "Hush logs your purchases from your bank's alert emails.",
  'It never asks for your bank login.',
  'Setup takes about five minutes on a computer, once.',
  'Hush works with Gmail for now.',
].join(' ');

export const WHICH_EMAIL_TITLE = 'Which email is which';
export const WHICH_GMAIL = 'Your Gmail: the inbox where your bank sends its alerts. Gmail does the forwarding.';
export const whichHush = (address: string) => `Your Hush address: ${address}. It is only where Gmail forwards alerts. Never enter it at your bank.`;
/** The screen shows the address with a Copy button between these two parts. */
export const WHICH_HUSH_LABEL = 'Your Hush address:';
export const WHICH_HUSH_NOTE = 'It is only where Gmail forwards alerts. Never enter it at your bank.';

export const STEP_TITLES = [
  'Turn on purchase alerts at your bank',
  'Let Gmail forward to Hush',
  'Choose which emails Gmail forwards',
  'Check it works',
] as const;

export type StepNumber = 1 | 2 | 3 | 4;

// ---- Step 1 ---------------------------------------------------------------------------

export const STEP1_DO = 'Tick your bank. Then follow its steps at your bank so it emails your Gmail about every purchase.';
export const STEP1_NEVER = 'Use your Gmail address at your bank. Never enter your Hush address there. Banks do not reliably send alerts to a second address.';
export const STEP1_HUSH = 'Hush ticks this step when a bank is on your list.';
export const NOT_LISTED = 'My bank is not listed';

// ---- Step 2 ---------------------------------------------------------------------------

/** Numbered steps with what the user will see. The Hush address is filled in. */
export const gmailForwardSteps = (address: string) => [
  'Open Gmail on a computer.',
  'Click Settings (the gear), then See all settings.',
  'Open the Forwarding and POP/IMAP tab.',
  'Click Add a forwarding address.',
  `Paste your Hush address: ${address}`,
  'Click Next, then Proceed.',
];
export const STEP2_CODE_SCREEN = 'Gmail sends a code to Hush. It appears right here within about a minute.';
export const STEP2_CODE_EMAIL = 'Gmail sends a code to Hush. It appears in the Hush app, on this same step, within about a minute.';
export const STEP2_VERIFY = 'Paste the code into Gmail and click Verify.';
export const STEP2_LEAVE = 'Leave Disable forwarding selected. The filter in step 3 forwards only your bank\'s alert emails.';
export const STEP2_RESEND = 'No code after two minutes? In Gmail, click Resend verification email.';
export const STEP2_HUSH = 'Hush ticks this step when the code arrives.';

// ---- Step 3 ---------------------------------------------------------------------------

export const COMPUTER_NOTE = 'Do this on a computer.';
export const FILTER_STEPS = [
  'In Gmail, click the search options icon in the search box.',
  'Paste each field into the Gmail box named next to it.',
  'Click Create filter.',
  'Tick Forward it to, and choose your Hush address.',
  'Click Create filter.',
];
export const FILTER_IMPORT = 'Or download the filter file. In Gmail, open Settings, See all settings, Filters and Blocked Addresses, then Import filters.';
export const STEP3_HUSH = 'Hush cannot see your Gmail filters. This step turns green when the first alert your filter forwards reaches Hush.';

/** The Gmail box each field goes in, with the value to paste. Empty ones say so. */
export function filterFields(f: GmailFilter): { box: string; value: string }[] {
  return [
    { box: 'From', value: f.from },
    { box: 'Subject', value: f.subject },
    { box: 'Has the words', value: f.hasWords },
  ];
}
export const LEAVE_EMPTY = 'leave empty';

/** "My bank is not listed": one real alert, forwarded by hand. */
export const firstAlertSteps = (address: string) => [
  'In your Gmail inbox find one purchase alert from your bank. No alert yet? Make a small purchase and wait for it.',
  `Open it, tap the three dots, tap Forward, and send it to ${address}`,
  'Come back here. Hush shows what it read, and you check it and tap Confirm.',
];
export const FIRST_ALERT_TITLE = 'Bank not listed: teach Hush with one alert';
export const NOT_A_CARD_ALERT =
  'That email did not read as a purchase alert, so Hush kept nothing. Forward an email your bank sent about a card purchase, not a statement, payment or sign-in notice.';

/** The registry promotion setting, in one plain sentence. */
export const SHARE_BANKS_LABEL = 'Share bank alert addresses';
export const SHARE_BANKS =
  'When two or more Hush users confirm the same bank alert address, Hush adds it for everyone. Only the address and subject word are shared, never your mail.';

// ---- Step 4 ---------------------------------------------------------------------------

/** Step 4 has no tick of its own: each bank shows its own status. */
export const STEP4_DO = "Make a small purchase. Hush shows when each bank's alert arrives.";
export const STEP4_WAITING = 'Waiting for an alert';
export const lastAlertText = (when: string) => `Last alert ${when}`;
export const silenceText = (bank: string, days: number) => `No ${bank} alerts in ${days} days. Check your Gmail filter.`;

// ---- The email -----------------------------------------------------------------------------

export interface SetupEmailInput {
  forwardAddress: string;
  /** The user's banks, in list order, each with its steps. */
  banks: { name: string; steps: string[] }[];
  /** The filter built from those banks, or null when none can be in a filter yet. */
  filter: GmailFilter | null;
}

/**
 * "Email these steps to me": the screen's wording in the screen's order, with real values
 * filled in. Opens in the user's own mail app; Hush sends nothing.
 */
export function setupEmail(input: SetupEmailInput): { subject: string; body: string } {
  const a = input.forwardAddress;
  const numbered = (xs: string[]) => xs.map((x, i) => `${i + 1}. ${x}`);
  const lines: string[] = [SETUP_INTRO, '', `${WHICH_EMAIL_TITLE}:`, WHICH_GMAIL, whichHush(a), ''];

  lines.push(`Step 1. ${STEP_TITLES[0]}`, STEP1_NEVER);
  if (input.banks.length) for (const b of input.banks) lines.push('', `${b.name}:`, ...numbered(b.steps));
  else lines.push('', ...numbered(GENERIC_BANK_STEPS));
  lines.push('');

  lines.push(`Step 2. ${STEP_TITLES[1]}`, ...numbered([...gmailForwardSteps(a), STEP2_CODE_EMAIL, STEP2_VERIFY]), STEP2_LEAVE, STEP2_RESEND, '');

  lines.push(`Step 3. ${STEP_TITLES[2]}`, COMPUTER_NOTE);
  if (input.filter) {
    lines.push(...numbered(FILTER_STEPS), '');
    for (const f of filterFields(input.filter)) lines.push(`${f.box}: ${f.value || LEAVE_EMPTY}`);
  } else {
    lines.push(...numbered(firstAlertSteps(a)));
  }
  lines.push('', `Step 4. ${STEP_TITLES[3]}`, STEP4_DO);
  return { subject: 'Hush setup steps', body: lines.join('\n') };
}


export const setupMailto = (input: SetupEmailInput) => {
  const { subject, body } = setupEmail(input);
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};
